import React from 'react';
import styled, { css } from 'styled-components';
import { media } from '@peloton/styles/breakpoints';
import { isDefined } from '@peloton/types/isDefined';
import { track } from '@engage/analytics/actions';
import { gray4, grey70 } from '@engage/colors/base';
import { If } from '@engage/conditional-render/If';
import type { MemberCard } from '@engage/members/MemberCard';
import { useReduxAction, useReduxState } from '@engage/redux/hooks';
import { header5Large, header5Small } from '@engage/typography/headers';
import { MembersCopy } from '@members/copy/copy.generated';
import { Members } from '@members/copy/models.generated/Members';
import {
  InviteFriendsContent,
  ContentType,
} from '@members/guest-pass/components/InviteFriendsContent';
import { getIsElligibleGuestPassUser } from '@members/guest-pass/redux/guestPassSku';
import { clearSearchResults } from '@members/member-search/redux';
import MemberList from '@members/members/MemberList';
import {
  MembersListModalContainer,
  ScrollContainer,
} from '@members/members/membersListModal';
import { getActiveModalSource } from '@members/modal/redux';
import { trackOpenMemberSearch } from './analytics';
import SearchIcon from './icons/SearchIcon';
import type { SearchControlProps } from './MemberSearchControls';
import { MemberSearchControls } from './MemberSearchControls';

export type MemberSearchProps = SearchControlProps & {
  searchResults?: MemberCard[];
};

export const MemberSearch: React.FC<React.PropsWithChildren<MemberSearchProps>> = ({
  searchResults,
  handleFbClick,
  inputValue,
  handleClickClear,
  handleInputChange,
}) => {
  const source = useReduxState(getActiveModalSource);
  const clearSearch = useReduxAction(clearSearchResults);
  const trackAction = useReduxAction(track);
  const isElligibleGuestPassUser = useReduxState(getIsElligibleGuestPassUser);

  React.useEffect(() => {
    if (!inputValue && isDefined(searchResults) && searchResults?.length > 0) {
      clearSearch();
    }
  }, [inputValue, clearSearch, searchResults]);

  React.useEffect(() => {
    trackAction(trackOpenMemberSearch(source));
  }, [source, trackAction]);

  return (
    <MembersListModalContainer data-test-id="findMembersModal">
      <MemberSearchControls
        inputValue={inputValue}
        handleClickClear={handleClickClear}
        handleInputChange={handleInputChange}
        handleFbClick={handleFbClick}
      />
      <If condition={!isDefined(searchResults)}>
        <FixedContainer>
          <SearchIconLarge data-test-id="searchIconLarge" />
        </FixedContainer>
      </If>
      <If condition={Number(searchResults?.length) > 0}>
        <ScrollContainer>
          <MemberList members={searchResults as MemberCard[]} />
        </ScrollContainer>
      </If>
      <If condition={isDefined(searchResults) && searchResults.length === 0}>
        {isElligibleGuestPassUser ? (
          <InviteFriendsContent
            contentType={ContentType.MembersSearch}
            onClose={handleClickClear}
          />
        ) : (
          <FixedContainer data-test-id="noMembersFound">
            <MembersCopy id={Members.NoMembersFound} />
          </FixedContainer>
        )}
      </If>
    </MembersListModalContainer>
  );
};

const mainContentArea = css`
  flex-grow: 1;
`;

const FixedContainer = styled.div`
  ${mainContentArea}
  ${header5Small}
  color: ${grey70};
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.tablet`
    ${header5Large}
    color: ${grey70};
    padding-bottom: 30px;
  `}
`;

const SearchIconLarge = styled(SearchIcon)`
  width: 80px;
  height: 80px;
  fill: ${gray4};

  ${media.desktop`
    width: 100px;
    height: 100px;
  `}
`;
